import { useState, useEffect, memo, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useReward } from 'react-rewards'
import {
  Navigate,
  Link,
  useParams,
  useLocation,
  Outlet,
} from 'react-router-dom-v5-compat'

import { useQuery } from '@apollo/client'
import { motion } from 'motion/react'

import {
  ArrowRightIcon,
  Badge,
  Button,
  ChannelAvatar,
  EmptyState,
  Flex,
  LockIcon,
  MessageCircleHeartIcon,
  SimpleSpinner,
  Skeleton,
  SkeletonText,
  Tabs,
  Text,
  type ChannelType,
} from '@buffer-mono/popcorn'

import { ChannelError } from '~publish/components/ChannelError'
import { ChannelNeedsRefresh } from '~publish/components/ChannelError/ChannelNeedsRefresh'
import { ChannelNotAvailable } from '~publish/components/ChannelError/ChannelNotAvailable'
import {
  getEngageUrl,
  requiresRefreshing,
} from '~publish/components/CommentsSidebar/ChannelNav/helpers'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { PageLayout } from '~publish/components/PageLayout'
import { graphql } from '~publish/gql'
import type {
  Channel,
  GetChannelInfoForCommentsQuery,
  SortDirection,
} from '~publish/gql/graphql'
import {
  commentsChannel,
  commentsPage,
  type CommentsChannelView,
} from '~publish/legacy/routes'
import { useCommentsConfig } from '~publish/pages/Comments/CommentsChannelConfigContext'

import { CommentsFilters } from '../CommentsFilters'
import { NewCommentsAvailableButton } from './NewCommentsAvailableButton'
import { useFilters } from '../CommentsFilters/useFilters'
import type {
  BaseCommentsFilters,
  DateRange,
  FilterStatus,
} from '../CommentsFilters/useFilters'
import { CommentsList } from '../CommentsList'
import { CommentsScorePopover } from '../CommentsScorePopover'
import { AnimatedNumber } from '../components/AnimatedNumber'
import { CommentsCount } from '../components/CommentsCount'
import {
  useChannelCommentCounts,
  type CommentCountsValues,
} from '../hooks/useChannelCommentCounts'
import { useCommentsScore } from '../hooks/useCommentsScore'
import { useCommentsTracking } from '../hooks/useCommentsTracking'
import { useIsFirstRender } from '../hooks/useIsFirstRender'
import { OverviewPage } from '../OverviewPage'
import { PostsList } from '../PostsList'
import { type CommentTab, availableTabs, DEFAULT_TAB } from '../types'
import {
  getScoreColors,
  type ScoreLevel,
  getScoreLevel,
  isHigherLevel,
} from '../util/commentScoreTheme'

import styles from './CommentsChannel.module.css'

export const ChannelPageCommentsFragment = graphql(/* GraphQL */ `
  fragment ChannelPageCommentsFragment on Channel {
    accessLevel
    id
    avatar
    isDisconnected
    isLocked
    name
    metadata {
      ... on LinkedInMetadata {
        __typename
        shouldShowLinkedinAnalyticsRefreshBanner
      }
    }
    locationData {
      location
    }
    serverUrl
    service
    timezone
    type
    scopes
  }
`)

export const GetChannelInfoForComments = graphql(/* GraphQL */ `
  query GetChannelInfoForComments($channelId: ChannelId!) {
    channel(input: { id: $channelId }) {
      ...ChannelPageCommentsFragment
      accessLevel
      id
      avatar
      isLocked
      name
      locationData {
        location
      }
      serverUrl
      service
      timezone
      type
      scopes
    }
  }
`)

const CommentsScore = ({ channelId }: { channelId: string }): JSX.Element => {
  const { isFirstRender } = useIsFirstRender()
  const { commentsScore, loading } = useCommentsScore({
    channelId,
  })
  const scoreValue = commentsScore?.currentWeekScore.totalValue
  const score = scoreValue ?? 0
  const [isOpen, setIsOpen] = useState(false)
  const [previousLevel, setPreviousLevel] = useState<ScoreLevel | undefined>(
    () => {
      if (scoreValue === undefined) {
        return undefined
      }

      return getScoreLevel(scoreValue)
    },
  )

  const { reward } = useReward('comments-score-celebration', 'confetti', {
    spread: 110,
    elementCount: 180,
    startVelocity: 35,
    lifetime: 80,
    decay: 0.8,
    colors: ['#5E68C2', '#FF3363', '#FFBB00', '#2AFF28', '#2AB6FF', '#EF0583'],
  })

  // Effect to detect level changes
  useEffect(() => {
    if (scoreValue === undefined) {
      return
    }

    const currentLevel = getScoreLevel(scoreValue)

    if (
      !isFirstRender &&
      previousLevel !== undefined &&
      currentLevel !== previousLevel &&
      isHigherLevel(currentLevel, previousLevel)
    ) {
      reward()
    }

    setPreviousLevel(currentLevel)
  }, [scoreValue, reward, previousLevel, isFirstRender, channelId])

  if (loading && !scoreValue) return <SimpleSpinner />

  if (!commentsScore) return <></>

  return (
    <CommentsScorePopover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      commentsScore={commentsScore}
    >
      <Flex
        direction="row"
        align="center"
        gap="3xs"
        className={styles.scoreContainer}
      >
        <Text color="subtle" size="sm" weight="medium">
          Comment score:
        </Text>
        <div id="comments-score-celebration" />
        <Badge size="small" className={styles[getScoreColors(score).className]}>
          <AnimatedNumber value={score} animateAsWhole />
        </Badge>
      </Flex>
    </CommentsScorePopover>
  )
}

export function ChannelPageTitle({
  channel,
}: {
  channel: GetChannelInfoForCommentsQuery['channel']
}): JSX.Element {
  if (!channel) {
    return <></>
  }

  const channelType: ChannelType = channel.service

  return (
    <Flex direction="row" align="center" gap="md">
      <ChannelAvatar
        src={channel.avatar}
        alt={channel.service}
        channel={channelType}
        size="medium"
      />
      <Flex direction="column" gap="2xs">
        <Flex direction="row" align="center" gap="xs">
          <PageLayout.Title>{channel.name}</PageLayout.Title>
        </Flex>
        <CommentsScore key={channel.id} channelId={channel.id} />
      </Flex>
    </Flex>
  )
}

const ChannelLockedError = (): JSX.Element => {
  return (
    <EmptyState size="large" variant="warning">
      <EmptyState.Icon>
        <LockIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>This channel is locked</EmptyState.Heading>
      <EmptyState.Description>
        Channel you are trying to access is locked. Likely it is because your
        account was downgraded.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button as={Link} to="/comments" size="large">
          <ArrowRightIcon />
          Go back to Community
        </Button>
      </EmptyState.Actions>
    </EmptyState>
  )
}

const CommentsCountDisplay = memo(
  ({
    channelId,
    counts,
  }: {
    channelId: string
    counts: CommentCountsValues[] | null
  }) => {
    if (!counts) return null

    const channelCount = counts.find((count) => count.channelId === channelId)
    return (
      <CommentsCount
        count={channelCount?.count ?? 0}
        variant="brand-subtle"
        size="small"
      />
    )
  },
)
CommentsCountDisplay.displayName = 'CommentsCountDisplay'

// Type guard functions to avoid type assertions
const isValidCommentTab = (value: string | null): value is CommentTab => {
  return value !== null && (value === 'overview' || value === 'comments')
}

const isValidCommentsChannelView = (
  value: string | undefined,
): value is CommentsChannelView => {
  return value === 'comments' || value === 'posts'
}

const PageLayoutMotion = motion.create(PageLayout)

export const CommentsChannel = (): JSX.Element => {
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')
  const tab: CommentTab = isValidCommentTab(tabParam) ? tabParam : DEFAULT_TAB
  const { channelId, view: viewParam } = useParams<{
    channelId: string
    view?: string
  }>()
  // Todo: Add setView back in when we have posts
  const [view] = useState<CommentsChannelView>(
    isValidCommentsChannelView(viewParam) ? viewParam : 'comments',
  )

  const { data, error } = useQuery<GetChannelInfoForCommentsQuery>(
    GetChannelInfoForComments,
    {
      variables: { channelId: channelId ?? '' },
      fetchPolicy: 'cache-and-network',
    },
  )

  const {
    filters: { status, sortDirection, publishedAt, dateStart, dateEnd },
    actions: { setStatus, setSortDirection, setPublishedAt },
    utils: { clearFilters, hasActiveFilters },
  } = useFilters()

  /* Add Tracking to filters */
  const { trackCommentsFiltered, trackCommentsSorted } = useCommentsTracking()

  const filters = useMemo<BaseCommentsFilters>(() => {
    return { status, sortDirection, dateStart, dateEnd }
  }, [status, sortDirection, dateStart, dateEnd])

  const channel = data?.channel

  const memoizedChannels = useMemo(() => (channel ? [channel] : []), [channel])
  const memoizedStatus = useMemo(
    () => (status && status !== 'all' ? [status] : undefined),
    [status],
  )

  const { counts } = useChannelCommentCounts({
    // The hook only uses channel.id, channel.service, and channel.type properties
    // which are all present in the ChannelPageCommentsFragment fragment
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    channels: memoizedChannels as Channel[],
    status: memoizedStatus,
    dateStart,
    dateEnd,
  })

  // Validate and redirect for invalid tabs
  useEffect(() => {
    if (tab && !availableTabs.includes(tab)) {
      // TO DO: update use of location change in Redux
      // https://linear.app/buffer/issue/CHAN-5410/redux-navigation-audit-document-use-of-push-in-middleware-and-location
      dispatch(
        commentsChannel.goToTab({
          channelId: channelId ?? '',
          tab: DEFAULT_TAB,
        }),
      )
    }
  }, [channelId, dispatch, tab])

  const handleStatusChange = (newStatus: FilterStatus): void => {
    setStatus(newStatus)
    if (!channel) return
    trackCommentsFiltered({
      channel: { id: channel.id, service: channel.service },
      filterType: 'status',
      filterValue: String(newStatus),
    })
  }

  const handleDateRangeChange = (newDateRange: DateRange): void => {
    setPublishedAt(newDateRange)
    if (!channel) return
    trackCommentsFiltered({
      channel: { id: channel.id, service: channel.service },
      filterType: 'date',
      filterValue: String(newDateRange),
    })
  }

  const handleSortDirectionChange = (newSortDirection: SortDirection): void => {
    setSortDirection(newSortDirection)
    if (!channel) return
    trackCommentsSorted({
      channel: { id: channel.id, service: channel.service },
      sortType: 'date',
      sortValue: String(newSortDirection),
    })
  }

  const handleTabChange = (newTab: CommentTab): void => {
    // TO DO: update use of location change in Redux
    // https://linear.app/buffer/issue/CHAN-5410/redux-navigation-audit-document-use-of-push-in-middleware-and-location
    dispatch(
      commentsChannel.goToTab({
        channelId: channelId ?? '',
        tab: newTab,
      }),
    )
  }

  const {
    config: { isSupportedInComments, isSupportedInEngage },
  } = useCommentsConfig()

  if (error) {
    return <ChannelError error={error} />
  }

  if (!channel) {
    return (
      <PageLayout>
        <PageLayout.Header>
          <PageLayout.HeaderRow>
            <Flex direction="row" align="center" gap="md">
              <Skeleton rounded width={48} height={48} />
              <Skeleton rounded width={100} height={20} />
            </Flex>
            <PageLayout.Actions>
              <Skeleton width={90} height={40} />
            </PageLayout.Actions>
          </PageLayout.HeaderRow>
          <PageLayout.HeaderRow>
            <Flex direction="row" align="center" gap="md">
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
            </Flex>
            <PageLayout.Actions>
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
            </PageLayout.Actions>
          </PageLayout.HeaderRow>
        </PageLayout.Header>
        <PageLayout.Container size="narrow">
          {/* <CommentsListSkeleton /> */}
        </PageLayout.Container>
      </PageLayout>
    )
  }

  if (!isSupportedInComments(channel)) {
    if (isSupportedInEngage(channel)) {
      window.location.href = getEngageUrl(channel.id)
      return <></>
    }

    return <ChannelNotAvailable channel={channel} />
  }

  if (channel.isLocked) {
    return <ChannelLockedError />
  }

  if (requiresRefreshing(channel)) {
    return <ChannelNeedsRefresh channel={channel} />
  }

  if (channel.accessLevel === 'none') {
    return <Navigate to={commentsPage.route} />
  }

  // Todo: Add back in when we have posts
  /*
  const handleToggleGroupChange = (value: string): void => {
    if (!value) return
    if (value !== view) {
      setView(value as CommentsChannelView)
    }
  }
  */

  return (
    <>
      <Outlet />
      <PageLayoutMotion layoutScroll>
        <Tabs
          defaultValue={DEFAULT_TAB}
          value={tab}
          onChange={(tab): void => {
            if (typeof tab === 'string' && isValidCommentTab(tab)) {
              handleTabChange(tab)
            }
          }}
        >
          <PageLayout.Header>
            <PageLayout.HeaderRow>
              <ChannelPageTitle channel={channel} />
              <PageLayout.Actions>
                <FeedbackWidget id="comments-channel-1" source="comments">
                  <Button variant="tertiary" size="large">
                    <MessageCircleHeartIcon />
                    Share Feedback
                  </Button>
                </FeedbackWidget>
                {/* Todo: Add back in when we have posts */}
                {/* tab === 'comments' && channelId && (
                <CommentsOrPostsViewToggle
                  channelId={channelId}
                  value={view}
                  handleToggleGroupChange={handleToggleGroupChange}
                />
              )}
              */}
              </PageLayout.Actions>
            </PageLayout.HeaderRow>
            <PageLayout.HeaderRow>
              <Tabs.TabList>
                {availableTabs.map((tab) => (
                  <Tabs.Tab key={tab} value={tab}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}{' '}
                    {tab === 'comments' && (
                      <CommentsCountDisplay
                        channelId={channel.id}
                        counts={counts}
                      />
                    )}
                  </Tabs.Tab>
                ))}
              </Tabs.TabList>
              <PageLayout.Actions>
                {tab === 'comments' && (
                  <CommentsFilters
                    status={status}
                    sortDirection={sortDirection}
                    publishedAt={publishedAt}
                    setStatus={handleStatusChange}
                    setSortDirection={handleSortDirectionChange}
                    setPublishedAt={handleDateRangeChange}
                    clearFilters={clearFilters}
                    channelId={channelId ?? ''}
                  />
                )}
              </PageLayout.Actions>
              <NewCommentsAvailableButton
                channelId={channelId ?? ''}
                filters={filters}
              />
            </PageLayout.HeaderRow>
          </PageLayout.Header>
          <div className={styles.container}>
            <Tabs.Panel value="overview" className={styles.panel}>
              <PageLayout.Container size="wide" as="main">
                {/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
                <OverviewPage channel={channel as Channel} />
              </PageLayout.Container>
            </Tabs.Panel>
            <Tabs.Panel value="comments" className={styles.panel}>
              <PageLayout.Container size="narrow" as="main">
                {view === 'posts' ? (
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  <PostsList channel={channel as Channel} />
                ) : (
                  <CommentsList
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    channel={channel as Channel}
                    filters={filters}
                    hasActiveFilters={hasActiveFilters()}
                    onClearFilters={clearFilters}
                  />
                )}
              </PageLayout.Container>
            </Tabs.Panel>
          </div>
        </Tabs>
      </PageLayoutMotion>
    </>
  )
}
